import { castToDate } from '@/utils/date.utils'
import BankDetails from '@/modules/common/hiring/models/APP/BankDetails'
import Insurance from '@/modules/common/common/models/APP/Insurance'
import Insured from '@/modules/common/common/models/APP/Insured'
import ProcessStatus from '@/modules/common/hiring/models/APP/ProcessStatus'
import { UserRoleEnum } from '@/modules/core/models/enums'

export default abstract class HiringAnswers<I extends Insured = Insured> {
  public quotationId: string | null
  public businessLine: string | null
  public billingCycle: string | null
  public product: Insurance | null

  protected _processStatus = new ProcessStatus()
  protected _price!: string
  protected _totalPrice!: string
  public insuranceTypes?: number[]
  protected declare _insureds: I[]
  protected declare _holder: I
  protected declare _currentInsured?: I | null
  protected _startDate!: Date
  protected _bankDetails = new BankDetails()

  constructor(data: Partial<HiringAnswers<I>> = {}) {
    this.quotationId = data.quotationId ?? null
    this.businessLine = data.businessLine ?? null
    this.billingCycle = data.billingCycle ?? null
    this.price = data.price
    this.totalPrice = data.totalPrice
    this.insuranceTypes = data?.insuranceTypes ?? []
    this.product = data.product ?? null
    this.processStatus = data.processStatus

    this.startDate = data.startDate
    this.bankDetails = data.bankDetails
  }

  get policyHolderPostal() {
    return this.holder.addressDetails?.postalCode
  }

  get processStatus(): ProcessStatus {
    return this._processStatus
  }

  set processStatus(value: ProcessStatus | undefined) {
    this._processStatus.updateAttributes(value ?? {})
  }

  set price(value: string | undefined) {
    this._price = value ?? '0.0'
  }
  get price(): string {
    return this._price
  }

  set totalPrice(value: string | undefined) {
    this._totalPrice = value ?? '0.0'
  }
  get totalPrice(): string {
    return this._totalPrice
  }

  abstract get insureds(): I[]
  abstract set insureds(value: I[] | undefined)

  abstract get holder(): I
  abstract set holder(value: I | undefined)

  abstract get currentInsured(): I | null
  abstract set currentInsured(value: I | null | undefined)

  get startDate(): Date {
    return this._startDate
  }
  set startDate(value: Date | string | undefined) {
    this._startDate = castToDate(value ?? null) ?? new Date()
  }

  get bankDetails(): BankDetails {
    return this._bankDetails
  }
  set bankDetails(value: BankDetails | undefined) {
    this._bankDetails.updateAttributes(value ?? {})
  }

  updateAttributes(newAttributes: Partial<HiringAnswers<I>>) {
    Object.assign(this, newAttributes)
  }

  updateHolderAttributes(holderData: Partial<I>) {
    this.holder.updateAttributes(holderData)

    if (this.holder.role === UserRoleEnum.HOLDER_INSURED) {
      this.insureds[0] = this.holder
    }
  }

  updateInsuredAttributes(insuredIndex: number, insuredData: Partial<I>) {
    const holderNotInsured = this.holder.role === UserRoleEnum.HOLDER_NOT_INSURED
    if (
      (this.holder.role === UserRoleEnum.HOLDER_INSURED || holderNotInsured) &&
      insuredIndex === 0
    ) {
      this.holder.updateAttributes(insuredData)
      return
    }

    this.insureds[holderNotInsured ? insuredIndex - 1 : insuredIndex].updateAttributes(insuredData)
  }

  isDocumentNumberAvailable(number: string, riskNumber: number, role: string): boolean {
    for (const insured of this._insureds) {
      if (
        insured.riskNumber !== riskNumber &&
        insured.addressDetails &&
        insured.documentDetails.documentNumber === number
      ) {
        return false
      }
    }
    if (this.holder.documentDetails.documentNumber === number && role !== this.holder.role)
      return false
    return true
  }

  /**
   * default to false, implement this in child class if needed
   *
   * @returns boolean
   */
  hasBetterAssurance(): boolean {
    return false
  }

  /**
   * default to false, implement this in child class if needed
   *
   * @returns boolean
   */
  hasAnotherCompany(): boolean {
    return false
  }
}
